import React, { FC, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Box, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { DataTable, Chart, CustomHorizontalBar, CustomDonutChart } from '../../../../../PDFViewer/components';
import { ApexLineChart } from '../../../../../PDFViewer/components/ApexChart';
import { AppContext } from '../../../../../../context';
import { API_SERVER } from '../../../../../../contants';
import styles, { inlineStyles } from './styles';
import moment from 'moment';
import ApexCharts from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { client } from '../../../../../../App';
import { GET_AUTOMATED_BAR_CHART, GET_AUTOMATED_COLUMNS } from '../../../../../../queries/widgets';
import { normalizeMonthlyCommentary, type ShareClassVisibilityMode } from '../mappers';
import { buildRollingPeriodLabels, buildAlignedAutomatedBarChartSeries, formatBarChartPeriodLabel } from '../../../../../../utils/formatBarChartPeriodLabel';
import { normalizeAutomatedColumnsInput } from '../../../../../../utils/normalizeAutomatedColumnsInput';
import { resolvePieChartHeading, type PieChartHeadingKey } from '../pieChartHeadings';
import { getPieChartDisplayOrder, type PiePreviewImageKey } from '../pieChartDisplay';
import {
  formatFactsheetHeaderDate,
  formatDateToUntil,
  getPreiseDisplayRows,
  resolveBarChartUntilIso,
  resolveFactsheetUntilIso,
} from '../factsheetDates';
import { normalizeRollingPerformance } from '../rollingPerformance';
import { formatDualSharePriceValue, isDualSharePriceField, resolveDualSharePriceRaw } from '../preiseFields';
import { TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE, TEMPLATE_ID_DUAL_SHARE_CLASS, resolveDisclaimerText, resolveFussnoteText, resolveManImageUrl } from '../templateConstants';
import { pvmWebDataTableSx } from '../../69d0c43f5a2b1c0d9e8f7a6b/pvmCompactLayout';
import {
  isPvmGpsTemplate,
  resolvePvmGpsFondTitle,
  resolvePvmGpsHistoryLegendLabel,
} from '../../69d0c43f5a2b1c0d9e8f7a6b/templateConstants';
import { GpsPage2RightColumn, GPS_DONUT_ASSET_ID, GPS_DONUT_SECTOR_ID } from '../../69d0c43f5a2b1c0d9e8f7a6b/gpsPage2';
import { PvmPage1LeftColumnWeb } from '../../69d0c43f5a2b1c0d9e8f7a6b/pvmPage1LeftColumn';
import { PvmPage2AwardImagesRowWeb, PvmPage2LeftWeb } from '../../69d0c43f5a2b1c0d9e8f7a6b/pvmPage2Left';
import {
  PVM_GPS_CONTACT_LINES,
  PVM_GPS_RIGHT_LOGO,
  PVM_HEADER_LEFT_LOGO_WIDTH_WEB,
  PVM_HEADER_SIDE_COLUMN_WIDTH_WEB,
  PVM_HEADER_PADDING_BELOW_TOP_BORDER_WEB,
  PVM_HEADER_TOP_LOGO_HEIGHT_WEB,
  PVM_HEADER_TOP_LOGO_WIDTH_WEB,
  resolvePvmGpsLeftLogoUrl,
} from '../../69d0c43f5a2b1c0d9e8f7a6b/pvmHeader';
import {
  PVM_BODY_FONT_STACK,
  PVM_DONUT_COLORS,
  PVM_HEADER_TITLE_FONT_SIZE_WEB,
  PVM_HEADLINE_COLOR,
  PVM_HEADLINE_FONT_STACK,
  pvmPaperFontSx,
  resolvePvmHeadlineColor,
  resolvePvmBodyColor,
  withPvmBodyStyle,
  withPvmHeadlineStyle,
} from '../../69d0c43f5a2b1c0d9e8f7a6b/pvmBranding';
import { resolveTitleAddition } from '../titleAddition';
import { getStammdatenRowLabel } from '../stammdatenFieldLabels';
import { getKonditionenDisplayKey, KONDITIONEN_TER_ROW_LABEL } from '../konditionenFieldLabels';
import { normalizeMultilineFieldValue, templateShowsAnlageberater } from '../anlageberater';
import { dualShareWebCompact, isDualShareCompactLayout, resolvePage1WebCompact } from '../dualShareCompactLayout';
import {
  calcMonthlyCommentaryImageWidthWeb,
  calcPvmMonthlyCommentaryFontSizeWeb,
} from '../monthlyCommentaryImage';

/** Dual share-class PREISE/KONDITIONEN/STAMMDATEN: same 3-column flex as react-pdf (no Table). */
const dualSharePreviewTripleBase = {
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
    mb: 0.5,
    fontSize: 14,
  },
  label: {
    width: '36%',
    flexShrink: 0,
    color: '#808080',
    pr: 1,
    lineHeight: 1.35,
  },
  value: {
    width: '32%',
    flexShrink: 0,
    color: '#000',
    fontWeight: 500,
    lineHeight: 1.2,
    whiteSpace: 'pre-line',
  },
  header: {
    width: '32%',
    flexShrink: 0,
    color: '#808080',
    fontWeight: 600,
    pb: 0.5,
    lineHeight: 1.35,
  },
  singleHeader: {
    width: '64%',
    flexShrink: 0,
    color: '#808080',
    fontWeight: 600,
    pb: 0.5,
    lineHeight: 1.35,
  },
  singleValue: {
    width: '64%',
    flexShrink: 0,
    color: '#000',
    fontWeight: 500,
    lineHeight: 1.2,
    whiteSpace: 'pre-line',
  },
};

function getDualSharePreviewTriple(compact: boolean) {
  if (!compact) return dualSharePreviewTripleBase;
  const lh = dualShareWebCompact.dualShareLineHeight;
  return {
    ...dualSharePreviewTripleBase,
    row: { ...dualSharePreviewTripleBase.row, mb: dualShareWebCompact.dualShareRowMarginBottom, lineHeight: lh },
    label: { ...dualSharePreviewTripleBase.label, lineHeight: lh },
    value: { ...dualSharePreviewTripleBase.value, lineHeight: lh },
    header: { ...dualSharePreviewTripleBase.header, lineHeight: lh, pb: 0.35 },
    singleHeader: { ...dualSharePreviewTripleBase.singleHeader, lineHeight: lh, pb: 0.35 },
    singleValue: { ...dualSharePreviewTripleBase.singleValue, lineHeight: lh },
  };
}

/** True when a formatted cell is empty, whitespace-only, or a common placeholder. */
function isFactsheetDisplayEmpty(display: string): boolean {
  const t = display.replace(/\r?\n+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!t) return true;
  if (t === '-' || t === '–' || t === '—') return true;
  if (/^n\/a$/i.test(t)) return true;
  return false;
}

/** Hide dual-column row when both cells are effectively empty. */
function isDualRowHidden(a: string, b: string): boolean {
  return isFactsheetDisplayEmpty(a) && isFactsheetDisplayEmpty(b);
}

export interface SectionHeaders {
  investmentphilosophie: string;
  strategie: string;
  preise: string;
  konditionen: string;
  stammdaten: string;
  wertentwicklungSeitAuflage: string;
  wertentwicklungNachJahren: string;
  wertentwicklungNachPerioden: string;
  risikoindikatoren: string;
  chancen: string;
  risiken: string;
  ethik: string;
  businessTable: string;
  monthlyCommentary: string;
  /** PVM/GPS page-2 section titles (optional; SHV templates omit these). */
  assetAllocation?: string;
  sectorAllocation?: string;
  top10Stocks?: string;
}

export const SECTION_HEADER_DEFAULTS: SectionHeaders = {
  investmentphilosophie: 'INVESTMENTPHILOSOPHIE',
  strategie: 'STRATEGIE',
  preise: 'PREISE',
  konditionen: 'KONDITIONEN',
  stammdaten: 'STAMMDATEN',
  wertentwicklungSeitAuflage: 'WERTENTWICKLUNG SEIT AUFLAGE',
  wertentwicklungNachJahren: 'WERTENTWICKLUNG NACH JAHREN (%)',
  wertentwicklungNachPerioden: 'WERTENTWICKLUNG NACH PERIODEN',
  risikoindikatoren: 'RISIKOINDIKATOREN',
  chancen: 'CHANCEN',
  risiken: 'RISIKEN',
  ethik: 'ETHIK',
  businessTable: 'KONTROVERSE GESCHÄFTSFELDER',
  monthlyCommentary: 'MONATSKOMMENTAR',
  assetAllocation: 'VERMÖGENSAUFTEILUNG',
  sectorAllocation: 'BRANCHEN (AKTIENANTEIL)',
  top10Stocks: 'TOP-10 AKTIEN (in %)',
};

interface FactsheetPreviewProps {
  data?: any;
  template?: any;
  lineChartLoading?: boolean;
  isPreviewStarted?: boolean;
  onChartImageChange?: (dataUrl?: string) => void;
  onChart1ImageChange?: (dataUrl?: string) => void;
  onChart2ImageChange?: (dataUrl?: string) => void;
  onChart3ImageChange?: (dataUrl?: string) => void;
  onPieChart1ImageChange?: (dataUrl?: string) => void;
  onPieChart2ImageChange?: (dataUrl?: string) => void;
  onPieChart3ImageChange?: (dataUrl?: string) => void;
  onPieChartImagesChange?: (countryChartUrl: string | undefined, sectorChartUrl: string | undefined) => void;
  onResetPreviewStarted?: (value: boolean) => void;
  onDataChange?: (data: any) => void;
  onRegisterChartCapture?: (capture: () => Promise<void>) => void;
}

export const FactsheetPreview: FC<FactsheetPreviewProps> = ({
  data,
  template: templateProp,
  lineChartLoading = false,
  onChartImageChange,
  onChart1ImageChange,
  onChart2ImageChange,
  onChart3ImageChange,
  onPieChart1ImageChange,
  onPieChart2ImageChange,
  onPieChart3ImageChange,
  onRegisterChartCapture,
}) => {
  const { document } = useContext(AppContext);
  const effectiveData = data || {};
  const template = templateProp || document?.template;
  const templateId = template?._id || document?.template?._id;
  const isPvmGps = isPvmGpsTemplate(templateId);
  const brandHeadlineColor = resolvePvmHeadlineColor(templateId, template?.color);
  const brandBodyColor = resolvePvmBodyColor(templateId);
  const contentTitleSx = useMemo(() => {
    if (!isPvmGps) return styles.contentTitle;
    return (theme: Parameters<typeof styles.contentTitle>[0]) =>
      withPvmHeadlineStyle(styles.contentTitle(theme) as Record<string, unknown>, true);
  }, [isPvmGps]);
  const contentTitle2Sx = useMemo(() => {
    if (!isPvmGps) return styles.contentTitle2;
    return (t: Parameters<typeof styles.contentTitle2>[0]) =>
      withPvmHeadlineStyle(styles.contentTitle2(t) as Record<string, unknown>, true);
  }, [isPvmGps]);
  const contentTextSx = useMemo(() => {
    if (!isPvmGps) return styles.contentText;
    return () => withPvmBodyStyle((styles.contentText as () => Record<string, unknown>)(), true);
  }, [isPvmGps]);
  const pdf = effectiveData;
  const monthlyCommentaryDisplay = normalizeMonthlyCommentary(pdf?.monthlyCommentary, template?._id);
  const manImageUrlResolved = resolveManImageUrl(template?._id, pdf?.manImageUrl, template?.manImageUrl);
  const manImageIsPlaceholder =
    !manImageUrlResolved ||
    manImageUrlResolved === '/images/intro.png' ||
    manImageUrlResolved.endsWith('/images/intro.png');
  // SHV always shows the portrait slot; PVM only when a custom image is uploaded.
  const showIntroManImage = isPvmGps ? !manImageIsPlaceholder : true;
  const introTextSx = useMemo(() => {
    if (!isPvmGps) return styles.introText;
    const fontSize = calcPvmMonthlyCommentaryFontSizeWeb(
      monthlyCommentaryDisplay.left,
      monthlyCommentaryDisplay.right,
    );
    return () => ({
      ...withPvmBodyStyle((styles.introText as () => Record<string, unknown>)(), true),
      color: PVM_HEADLINE_COLOR,
      lineHeight: 1.15,
      fontSize,
    });
  }, [isPvmGps, monthlyCommentaryDisplay.left, monthlyCommentaryDisplay.right]);
  const paperSx = useMemo(
    () => (theme: Parameters<typeof styles.paper>[0]) => ({
      ...styles.paper(theme),
      ...(isPvmGps ? pvmPaperFontSx : {}),
      ...(isPvmGps ? { color: brandBodyColor } : {}),
    }),
    [isPvmGps, brandBodyColor],
  );
  const omitEthikKontroverse =
    templateId === TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE || isPvmGps;
  const dualShareClassLayout =
    template?._id === TEMPLATE_ID_DUAL_SHARE_CLASS &&
    Array.isArray(effectiveData?.shareClassColumns) &&
    effectiveData.shareClassColumns.length >= 2;
  const compactLayout = isDualShareCompactLayout(template?._id);
  const page1Compact = resolvePage1WebCompact(template?._id);
  const dualSharePreviewTriple = getDualSharePreviewTriple(compactLayout);
  const page1SectionMb = page1Compact?.sectionMarginBottom ?? 20;
  const page1GridSpacing = page1Compact?.mainGridSpacing ?? 50;
  const page1StackSpacing = page1Compact?.stackSpacing ?? 20;
  const page1LineChartHeight = page1Compact?.lineChartHeight ?? 260;
  const page1BarChartHeight = page1Compact?.barChartHeight ?? 230;
  const page2GridSpacing = page1Compact?.page2GridSpacing ?? 50;
  const compactContentTitleSx = useCallback(
    (theme: Parameters<typeof styles.contentTitle>[0]) =>
      withPvmHeadlineStyle(
        {
          ...styles.contentTitle(theme),
          mb: page1Compact?.contentTitleMarginBottom ?? dualShareWebCompact.contentTitleMarginBottom,
          pb: page1Compact?.contentTitlePaddingBottom ?? dualShareWebCompact.contentTitlePaddingBottom,
        },
        isPvmGps,
      ),
    [page1Compact, isPvmGps],
  );
  const pieHeading = useCallback(
    (key: PieChartHeadingKey) =>
      resolvePieChartHeading(template?._id, key, {
        headings: pdf?.headings,
        pieTitles: pdf?.pie_titles,
      }),
    [template?._id, pdf?.headings, pdf?.pie_titles],
  );
  const isin = template?.isins?.[0] || '';

  const untilFormatted = useMemo(
    () => resolveFactsheetUntilIso(pdf) || formatDateToUntil(pdf?.published_at),
    [pdf?.published_at, pdf?.historyUntil, pdf?.data_as_of, pdf?.preise?.kursper],
  );
  const barChartUntilFormatted = useMemo(
    () => resolveBarChartUntilIso(pdf),
    [pdf?.published_at, pdf?.historyUntil, pdf?.data_as_of, pdf?.preise?.kursper],
  );

  // Chart data and config (same pattern as other templates)
  const lineChartData = pdf?.lineChartData || [];
  const historyTableLabels = pdf?.wertentwicklung_seit_auflage_labels || [];
  const fafsHistoryTableLabel: any = compactLayout
    ? {
        label: pdf?.historyLegendLabel || historyTableLabels?.[0]?.label || '',
        subLabel: pdf?.historyLegendSubLabel || historyTableLabels?.[0]?.subLabel || '',
      }
    : null;
  const historyLegendLabel: string = isPvmGps
    ? resolvePvmGpsHistoryLegendLabel(
        pdf?.historyLegendLabel || pdf?.wertentwicklung_seit_auflage_label,
      )
    : pdf?.historyLegendLabel || pdf?.wertentwicklung_seit_auflage_label || 'Frankfurter Modern Value Index';
  // Admin "Currency symbol (y-axis)" — drives preview + published PDF ticks.
  const historyCurrencySymbol: string = pdf?.historyCurrencySymbol || '€';

  // Color sets from template (used for donut charts, bar charts, etc.)
  const colorSets = useMemo(
    () =>
      isPvmGps
        ? PVM_DONUT_COLORS
        : (template?.colorSets ?? []),
    [isPvmGps, template?.colorSets],
  );

  // Section headers (editable via PdfConfig)
  const sectionHeaders: SectionHeaders = {
    ...SECTION_HEADER_DEFAULTS,
    ...(pdf?.sectionHeaders || {}),
  };

  // Rolling performance / bar chart config (automated IT4Funds feed by default)
  const rollingPerformance = useMemo(
    () => normalizeRollingPerformance(pdf?.rollingPerformance, isin),
    [pdf?.rollingPerformance, isin],
  );

  const rollingChartType = rollingPerformance.chartType;
  const rollingIsin = rollingPerformance.isin || isin;
  const rollingAutomationType = rollingPerformance.automationType;
  const rollingBarColorSets = rollingPerformance.barColorSets;

  // State for automated bar chart data
  const [automatedPeriodsData, setAutomatedPeriodsData] = useState<any[]>([{ name: '', data: [] }]);
  const [automatedPeriodLabels, setAutomatedPeriodLabels] = useState<string[]>([]);
  const [barChartLoading, setBarChartLoading] = useState(
    () => rollingChartType === 'Automated',
  );

  const barChartFetchKey = useMemo(
    () =>
      [
        rollingChartType,
        rollingIsin,
        rollingAutomationType,
        barChartUntilFormatted,
        pdf?.published_at,
      ].join('|'),
    [
      rollingChartType,
      rollingIsin,
      rollingAutomationType,
      barChartUntilFormatted,
      pdf?.published_at,
    ],
  );

  // Fetch automated bar chart data (cancelled flag avoids Strict Mode double-mount races)
  useEffect(() => {
    if (!rollingIsin || rollingChartType !== 'Automated') {
      setBarChartLoading(false);
      return;
    }

    let cancelled = false;
    // Show spinner immediately on first load and on Kurs-per / until changes.
    setBarChartLoading(true);

    const untilForApi = barChartUntilFormatted || formatDateToUntil(pdf?.published_at);

    const applyBarChartRows = (apiData: any[]) => {
      if (rollingAutomationType === 'all') {
        const mainColors = rollingBarColorSets.length
          ? rollingBarColorSets
          : colorSets.length >= 2
            ? colorSets.slice(0, 2).reverse()
            : [brandHeadlineColor, '#667a7a'];

        const { labels, series } = buildAlignedAutomatedBarChartSeries(apiData, {
          legend: rollingPerformance.legend,
          colors: mainColors,
          fallbackColor: brandHeadlineColor,
        });

        setAutomatedPeriodLabels(labels);
        setAutomatedPeriodsData(
          series.map((item) => ({
            name: item.name,
            data: item.data.map((value) => (value == null ? 0 : value)),
            color: item.color,
          })),
        );
      } else {
        const labelRows = [...apiData].sort(
          (a: any, b: any) =>
            moment(a.end, 'DD.MM.YYYY', true).valueOf() -
            moment(b.end, 'DD.MM.YYYY', true).valueOf(),
        );
        setAutomatedPeriodLabels(buildRollingPeriodLabels(labelRows));
        setAutomatedPeriodsData([{ name: '', data: labelRows.map((item: any) => item.value) }]);
      }
    };

    const loadBarChart = async () => {
      try {
        const queryBarChart = (useFactsheetUntil: boolean) =>
          client.query({
            query: GET_AUTOMATED_BAR_CHART,
            variables: {
              isin: rollingIsin,
              type: rollingAutomationType,
              ...(untilForApi ? { until: untilForApi } : {}),
              ...(useFactsheetUntil && untilForApi
                ? { useFactsheetUntilForCurrentYear: true }
                : {}),
            },
            fetchPolicy: 'network-only',
          });

        const result = await queryBarChart(Boolean(untilForApi));

        const apiData = result.data?.getAutomatedBarChart;

        if (cancelled) return;

        if (!Array.isArray(apiData) || apiData.length === 0) {
          setAutomatedPeriodLabels([]);
          setAutomatedPeriodsData([{ name: '', data: [] }]);
          return;
        }

        applyBarChartRows(apiData);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch automated bar chart data:', error);
          setAutomatedPeriodLabels([]);
          setAutomatedPeriodsData([{ name: '', data: [] }]);
        }
      } finally {
        if (!cancelled) {
          setBarChartLoading(false);
        }
      }
    };

    loadBarChart();

    return () => {
      cancelled = true;
    };
  }, [
    barChartFetchKey,
    barChartUntilFormatted,
    colorSets,
    pdf?.published_at,
    rollingAutomationType,
    rollingBarColorSets,
    rollingChartType,
    rollingIsin,
    rollingPerformance.legend,
    brandHeadlineColor,
  ]);

  // --- Dynamic table cell fetching (WERTENTWICKLUNG NACH PERIODEN & RISIKOINDIKATOREN) ---
  const [cellDataCache, setCellDataCache] = useState<Map<string, any>>(new Map());

  // Include dynamic config in cache keys so changing table/field triggers re-fetch
  const getDynamicSignature = useCallback((config: any): string => {
    try {
      return JSON.stringify(config?.fields || []);
    } catch {
      return '';
    }
  }, []);

  // Clear cache when until changes
  useEffect(() => {
    setCellDataCache(new Map());
  }, [untilFormatted]);

  // --- Dynamic key-value fields (Preise: mepreis, fondsvolumen) ---
  const [kvCache, setKvCache] = useState<Map<string, any>>(new Map());

  useEffect(() => {
    setKvCache(new Map());
  }, [untilFormatted, isin]);

  const resolveDynamicIsin = useCallback(
    (config: any, fallbackIsin?: string): string => {
      const base = fallbackIsin || isin;
      if (base) return base;
      const fieldWithIsin = config?.fields?.find(
        (field: any) => typeof field?.isin === 'string' && field.isin.trim().length > 0,
      );
      return fieldWithIsin?.isin?.trim() || '';
    },
    [isin],
  );

  const fetchKvValue = useCallback(
    async (config: any, key: string, columnIsin?: string, colSuffix = '') => {
      const effIsin = resolveDynamicIsin(config, columnIsin);
      if (!effIsin || !config?.isDynamic || !config?.fields?.length) return;
      const cacheKey = `kv-${key}-${untilFormatted || ''}-${getDynamicSignature(config)}-${colSuffix}`;
      if (kvCache.has(cacheKey)) return;
      try {
        const res = await client.query({
          query: GET_AUTOMATED_COLUMNS,
          variables: { param: { data: [[config]], isin: effIsin, until: untilFormatted } },
          fetchPolicy: 'network-only',
        });
        const fetchedValue = res.data.getAutomatedColumns?.[0]?.[0];
        setKvCache((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, fetchedValue);
          return next;
        });
      } catch (e) {
        // ignore fetch failures in preview
      }
    },
    [untilFormatted, kvCache, getDynamicSignature, resolveDynamicIsin],
  );

  useEffect(() => {
    const mepreisCfg = pdf?.preise?.mepreis;
    const fondsvolumenCfg = pdf?.preise?.fondsvolumen;
    const ausgabeCfg = pdf?.preise?.ausgabepreis;
    const ruecknahmeCfg = pdf?.preise?.ruecknahmepreis;
    if (dualShareClassLayout) {
      if (ausgabeCfg?.isDynamic) fetchKvValue(ausgabeCfg, 'preise.ausgabepreis');
      if (ruecknahmeCfg?.isDynamic) fetchKvValue(ruecknahmeCfg, 'preise.ruecknahmepreis');
    } else if (mepreisCfg?.isDynamic) {
      fetchKvValue(mepreisCfg, 'preise.mepreis');
    }
    // PVM left column uses ruecknahmepreis as NAV
    if (isPvmGps && ruecknahmeCfg?.isDynamic) {
      fetchKvValue(ruecknahmeCfg, 'preise.ruecknahmepreis');
    }
    if (fondsvolumenCfg?.isDynamic) fetchKvValue(fondsvolumenCfg, 'preise.fondsvolumen');
  }, [
    pdf?.preise?.mepreis,
    pdf?.preise?.ausgabepreis,
    pdf?.preise?.ruecknahmepreis,
    pdf?.preise?.fondsvolumen,
    fetchKvValue,
    untilFormatted,
    dualShareClassLayout,
    isPvmGps,
  ]);

  useEffect(() => {
    if (!dualShareClassLayout || !pdf?.shareClassColumns) return;
    pdf.shareClassColumns.forEach((col: any, i: number) => {
      const colIsin = String(col?.stammdaten?.isin || col?.isin || template?.isins?.[i] || isin);
      const suff = `sc${i}`;
      if (col?.preise?.ausgabepreis?.isDynamic) {
        fetchKvValue(col.preise.ausgabepreis, 'preise.ausgabepreis', colIsin, suff);
      }
      if (col?.preise?.ruecknahmepreis?.isDynamic) {
        fetchKvValue(col.preise.ruecknahmepreis, 'preise.ruecknahmepreis', colIsin, suff);
      }
      if (col?.preise?.mepreis?.isDynamic) {
        fetchKvValue(col.preise.mepreis, 'preise.mepreis', colIsin, suff);
      }
      if (col?.preise?.fondsvolumen?.isDynamic) fetchKvValue(col.preise.fondsvolumen, 'preise.fondsvolumen', colIsin, suff);
    });
  }, [dualShareClassLayout, pdf?.shareClassColumns, fetchKvValue, template?.isins, isin, untilFormatted]);

  const resolveKv = useCallback(
    (value: any, key: string, colSuffix = '') => {
      if (value?.isDynamic) {
        const cacheKey = `kv-${key}-${untilFormatted || ''}-${getDynamicSignature(value)}-${colSuffix}`;
        const fetched = kvCache.get(cacheKey);
        if (fetched !== undefined && fetched !== null) return String(fetched);
        return value?.fields?.[0]?.text || '';
      }
      return typeof value === 'string' ? value : (value ?? '');
    },
    [kvCache, untilFormatted, getDynamicSignature],
  );

  const resolvedPreise = useMemo(() => {
    const preise = pdf?.preise || {};
    const mepreis = resolveKv(preise?.mepreis, 'preise.mepreis');
    const fondsvolumen = resolveKv(preise?.fondsvolumen, 'preise.fondsvolumen');
    const ordered: [string, unknown][] = [
      ['wahrung', preise.wahrung],
      ['mepreis', mepreis],
      ['fondsvolumen', fondsvolumen],
      ['Ertragsart', preise.ertragsart ?? ''],
    ];
    const letzte = preise.letzte;
    if (letzte !== undefined && letzte !== null && String(letzte).trim() !== '') {
      ordered.push(['Letzte Ausschüttung', letzte]);
    }
    ordered.push(['ausschuttung', preise.ausschuttung]);
    const used = new Set(['wahrung', 'mepreis', 'fondsvolumen', 'ertragsart', 'letzte', 'ausschuttung']);
    for (const [k, v] of Object.entries(preise)) {
      if (used.has(k)) continue;
      ordered.push([k, v]);
    }
    return Object.fromEntries(ordered);
  }, [pdf?.preise, resolveKv]);

  /** PVM left column: materialize dynamic NAV / Fondsvermögen from kvCache */
  const pvmLeftColumnPdf = useMemo(() => {
    if (!isPvmGps || !pdf) return pdf || {};
    const preise = { ...(pdf.preise || {}) };
    const fondsvolumen = resolveKv(preise.fondsvolumen, 'preise.fondsvolumen');
    const ruecknahme = resolveKv(preise.ruecknahmepreis, 'preise.ruecknahmepreis');
    const mepreis = resolveKv(preise.mepreis, 'preise.mepreis');
    if (fondsvolumen && !preise.fondsvolumen?.isDynamic) {
      // already scalar
    } else if (fondsvolumen) {
      preise.fondsvolumen = fondsvolumen;
    }
    if (ruecknahme && String(ruecknahme).trim()) {
      preise.ruecknahmepreis = ruecknahme;
    } else if (mepreis && String(mepreis).trim()) {
      preise.ruecknahmepreis = mepreis;
    }
    return { ...pdf, preise };
  }, [isPvmGps, pdf, resolveKv]);

  const resolvedStammdaten = useMemo(() => {
    const s = pdf?.stammdaten || {};
    const showAnlageberater = templateShowsAnlageberater(templateId);
    return Object.fromEntries(
      Object.entries(s)
        .filter(([k]) => showAnlageberater || k !== 'anlageberater')
        .map(([k, v]) => (k === 'vertriebszulassung' ? ['Vertriebszulassung', v] : [k, v])),
    );
  }, [pdf?.stammdaten, templateId]);

  const resolvedKonditionen = useMemo(() => {
    const k = pdf?.konditionen || {};
    return Object.fromEntries(
      Object.entries(k).map(([key, v]) => [getKonditionenDisplayKey(key), v]),
    );
  }, [pdf?.konditionen]);

  const buildPreiseDisplay = useCallback(
    (preise: any, colSuff: string) => {
      const base = {
        ...preise,
        ...(dualShareClassLayout
          ? (() => {
              const combinedMepreis = resolveKv(preise?.mepreis, 'preise.mepreis', colSuff);
              return {
                ausgabepreis: resolveDualSharePriceRaw(
                  'ausgabepreis',
                  resolveKv(preise?.ausgabepreis, 'preise.ausgabepreis', colSuff),
                  combinedMepreis,
                ),
                ruecknahmepreis: resolveDualSharePriceRaw(
                  'ruecknahmepreis',
                  resolveKv(preise?.ruecknahmepreis, 'preise.ruecknahmepreis', colSuff),
                  combinedMepreis,
                ),
              };
            })()
          : {
              mepreis: resolveKv(preise?.mepreis, 'preise.mepreis', colSuff),
            }),
        fondsvolumen: resolveKv(preise?.fondsvolumen, 'preise.fondsvolumen', colSuff),
      };
      const mapped = Object.entries(base).map(([k, v]) => {
        if (k === 'letzte') return ['Letzte Ausschüttung', v];
        return [k, v];
      });
      const filtered = mapped.filter(([k, v]) => {
        if (k !== 'Letzte Ausschüttung') return true;
        return v !== undefined && v !== null && String(v).trim() !== '';
      });
      return Object.fromEntries(filtered);
    },
    [resolveKv, dualShareClassLayout],
  );

  const formatPreiseCell = (key: string, raw: unknown): string => {
    if (raw === undefined || raw === null) return '';
    if (isDualSharePriceField(key) || key === 'mepreis') {
      return formatDualSharePriceValue(raw);
    }
    if (key === 'fondsvolumen' && typeof raw === 'string' && /^[0-9]+$/.test(raw)) {
      return `${Number(raw).toLocaleString('de-DE')} EUR`;
    }
    return String(raw);
  };

  const dualShareColumnsData = pdf?.shareClassColumns;
  const dualVisibilityModeRaw =
    (pdf?.shareClassVisibilityMode as ShareClassVisibilityMode | undefined) ||
    ((pdf?.shareClassVisibility || {}) as Partial<Record<'preise' | 'konditionen' | 'stammdaten', ShareClassVisibilityMode>>)
      ?.preise ||
    'both';
  const dualVisibilityMode: ShareClassVisibilityMode =
    dualVisibilityModeRaw === 'column0' || dualVisibilityModeRaw === 'column1' ? dualVisibilityModeRaw : 'both';
  const getVisibleColumnIndices = useCallback(
    (): number[] => {
      if (dualVisibilityMode === 'column0') return [0];
      if (dualVisibilityMode === 'column1') return [1];
      return [0, 1];
    },
    [dualVisibilityMode],
  );

  // Fetch all dynamic cells for a table in one request (GraphQL expects a 2D data matrix)
  const fetchDynamicTable = useCallback(
    async (values: any[][], prefix: string) => {
      if (!values?.length || !Array.isArray(values)) return;

      const tableIsin =
        values
          .flat()
          .map((cell) => resolveDynamicIsin(cell))
          .find((value) => value.length > 0) || isin;
      if (!tableIsin) return;

      try {
        const res = await client.query({
          query: GET_AUTOMATED_COLUMNS,
          variables: {
            param: { data: normalizeAutomatedColumnsInput(values), isin: tableIsin, until: untilFormatted },
          },
          fetchPolicy: 'network-only',
        });
        const matrix = res.data?.getAutomatedColumns;
        if (!Array.isArray(matrix)) return;

        setCellDataCache((prev) => {
          const newCache = new Map(prev);
          values.forEach((row: any[], rowIndex: number) => {
            row.forEach((cell: any, colIndex: number) => {
              if (cell?.isDynamic && cell?.fields?.length) {
                const cellKey = `${prefix}-${rowIndex}-${colIndex}-${untilFormatted || ''}-${getDynamicSignature(cell)}`;
                newCache.set(cellKey, matrix[rowIndex]?.[colIndex]);
              }
            });
          });
          return newCache;
        });
      } catch (error) {
        console.error('Failed to fetch dynamic table data:', error);
      }
    },
    [isin, untilFormatted, getDynamicSignature, resolveDynamicIsin],
  );

  // Trigger fetch for performance table
  useEffect(() => {
    const perf = pdf?.wertentwicklung_nach_perioden;
    if (perf?.values) fetchDynamicTable(perf.values, 'perf');
  }, [pdf?.wertentwicklung_nach_perioden, fetchDynamicTable, untilFormatted]);

  // Trigger fetch for risk indicators table
  useEffect(() => {
    const risk = pdf?.risikoindikatoren;
    if (risk?.values) fetchDynamicTable(risk.values, 'risk');
  }, [pdf?.risikoindikatoren, fetchDynamicTable, untilFormatted]);

  /** Resolve cell display value, handling dynamic cells and different column types */
  const resolveCellDisplay = useCallback((cell: any, prefix: string, rowIndex: number, colIndex: number, columns?: any[]): string => {
    const columnType = columns?.[colIndex]?.type || 'text';
    const isDynamic = cell?.isDynamic && cell?.fields?.length;
    const cellKey = `${prefix}-${rowIndex}-${colIndex}-${untilFormatted || ''}-${getDynamicSignature(cell)}`;
    const fetchedValue = isDynamic ? cellDataCache.get(cellKey) : null;

    let display = '';
    if (isDynamic && fetchedValue !== undefined && fetchedValue !== null) {
      display = String(fetchedValue);
    } else if (columnType === 'text') {
      display = typeof cell === 'string' ? cell : (cell?.text || '');
    } else if (columnType === 'link') {
      display = cell?.linkText || '';
    } else if (columnType === 'avatar') {
      display = typeof cell === 'string' ? cell : '';
    } else {
      display = typeof cell === 'string' ? cell : '';
    }

    // Period returns are percentages — GraphQL formats the number without a % suffix
    if (prefix === 'perf' && colIndex > 0 && display && !display.trim().endsWith('%')) {
      display = `${display}%`;
    }
    return display;
  }, [cellDataCache, untilFormatted, getDynamicSignature]);

  // Bar chart options and series
  const isBarAutomated = rollingPerformance?.chartType === 'Automated';
  const barChartCategoriesRaw = useMemo(() => {
    if (isBarAutomated) {
      return automatedPeriodLabels;
    }
    return (rollingPerformance?.periods || []).map((p: any) => p.label);
  }, [isBarAutomated, automatedPeriodLabels, rollingPerformance?.periods]);

  const barChartCategories = barChartCategoriesRaw;

  const barChartOptions: ApexOptions = useMemo(() => ({
    chart: {
      id: 'bar-wertentwicklung',
      type: 'bar',
      height: 230,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '50%',
      },
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 0, colors: ['transparent'] },
    xaxis: {
      categories: barChartCategories,
      labels: {
        rotate: 0,
        rotateAlways: false,
        trim: false,
        hideOverlappingLabels: false,
        minHeight: 76,
        maxHeight: 120,
        offsetY: 4,
        formatter: (val: string) => formatBarChartPeriodLabel(String(val ?? '')),
        style: {
          fontSize: '9px',
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value: number) => `${value}%`,
      },
    },
    legend: {
      show: isBarAutomated ? rollingAutomationType === 'all' : false,
      position: 'bottom',
    },
    colors: isBarAutomated && rollingBarColorSets.length
      ? rollingBarColorSets
      : colorSets.length >= 2
        ? colorSets.slice(0, 2)
        : [brandHeadlineColor, '#667a7a'],
    fill: { opacity: 1 },
  }), [barChartCategories, isBarAutomated, rollingAutomationType, rollingBarColorSets, brandHeadlineColor, colorSets, compactLayout]);

  const barChartSeries = useMemo(() => {
    if (isBarAutomated) {
      if (automatedPeriodsData.length > 0 && automatedPeriodsData[0]?.data?.length > 0) {
        return automatedPeriodsData.map((series) => ({
          name: series.name || '',
          data: series.data,
        }));
      }
      return [];
    }
    if (rollingPerformance?.periods?.length > 0) {
      return [{
        name: '',
        data: rollingPerformance.periods.map((p: any) => ({
          x: p.label,
          y: p.value,
        })),
      }];
    }
    if (pdf?.wertentwicklung_nach_jahren?.length > 0) {
      return [{
        name: '',
        data: pdf.wertentwicklung_nach_jahren.map((item: any) => item.value),
        color: brandHeadlineColor,
      }];
    }
    return [];
  }, [isBarAutomated, automatedPeriodsData, barChartCategories, rollingPerformance?.periods, pdf?.wertentwicklung_nach_jahren, brandHeadlineColor]);

  // Check if bar chart has data to show
  const hasBarChartData = barChartSeries.length > 0 && barChartSeries.some((s: any) => s.data?.length > 0);

  const lineChartDataRef = useRef(lineChartData);
  const lineChartLoadingRef = useRef(lineChartLoading);
  const pdfDataRef = useRef(pdf);
  const hasBarChartDataRef = useRef(hasBarChartData);
  lineChartDataRef.current = lineChartData;
  lineChartLoadingRef.current = lineChartLoading;
  pdfDataRef.current = pdf;
  hasBarChartDataRef.current = hasBarChartData;

  const CHART_CAPTURE_INITIAL_DELAY_MS = 900;
  const CHART_CAPTURE_STEP_MS = 450;

  const execChartCapture = useCallback(async (chartId: string): Promise<string | undefined> => {
    try {
      const res: any = await (ApexCharts as any).exec(chartId, 'dataURI', { scale: 3 });
      return res?.imgURI;
    } catch {
      return undefined;
    }
  }, []);

  const waitForLineChartReady = useCallback(async (maxMs = 20000): Promise<boolean> => {
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      if (!lineChartLoadingRef.current) {
        return (lineChartDataRef.current?.length ?? 0) > 0;
      }
      await new Promise((r) => setTimeout(r, 150));
    }
    return (lineChartDataRef.current?.length ?? 0) > 0;
  }, []);

  const captureAllChartsForExport = useCallback(async () => {
    const pieHandlers: Record<PiePreviewImageKey, ((dataUrl?: string) => void) | undefined> = {
      pieChart1ImageDataUrl: onPieChart1ImageChange,
      pieChart2ImageDataUrl: onPieChart2ImageChange,
      pieChart3ImageDataUrl: onPieChart3ImageChange,
      chart3ImageDataUrl: onChart3ImageChange,
    };

    await waitForLineChartReady();
    await new Promise((r) => setTimeout(r, CHART_CAPTURE_INITIAL_DELAY_MS));

    if (lineChartDataRef.current?.length) {
      onChartImageChange?.(await execChartCapture('area-datetime'));
    } else {
      onChartImageChange?.(undefined);
    }

    if (hasBarChartDataRef.current) {
      await new Promise((r) => setTimeout(r, CHART_CAPTURE_STEP_MS));
      onChart1ImageChange?.(await execChartCapture('bar-wertentwicklung'));
    }

    if (isPvmGpsTemplate(template?._id)) {
      if (pdfDataRef.current?.assetAllocation?.length) {
        await new Promise((r) => setTimeout(r, CHART_CAPTURE_STEP_MS));
        onPieChart1ImageChange?.(await execChartCapture(GPS_DONUT_ASSET_ID));
      }
      if (pdfDataRef.current?.sectorAllocation?.length) {
        await new Promise((r) => setTimeout(r, CHART_CAPTURE_STEP_MS));
        onPieChart2ImageChange?.(await execChartCapture(GPS_DONUT_SECTOR_ID));
      }
    } else {
      if (pdfDataRef.current?.gesamtportfolio?.length) {
        await new Promise((r) => setTimeout(r, CHART_CAPTURE_STEP_MS));
        onChart2ImageChange?.(await execChartCapture('hbar-gesamtportfolio'));
      }

      for (const slot of getPieChartDisplayOrder(template?._id)) {
        const pieData = pdfDataRef.current?.[slot.field];
        if (!Array.isArray(pieData) || pieData.length === 0) continue;
        await new Promise((r) => setTimeout(r, CHART_CAPTURE_STEP_MS));
        pieHandlers[slot.previewImageKey]?.(await execChartCapture(slot.donutId));
      }
    }
  }, [
    execChartCapture,
    onChart1ImageChange,
    onChart2ImageChange,
    onChart3ImageChange,
    onChartImageChange,
    onPieChart1ImageChange,
    onPieChart2ImageChange,
    onPieChart3ImageChange,
    template?._id,
    waitForLineChartReady,
  ]);

  useEffect(() => {
    if (!onRegisterChartCapture) return;
    onRegisterChartCapture(captureAllChartsForExport);
  }, [onRegisterChartCapture, captureAllChartsForExport]);

  // Capture line chart image for PDF rendering
  useEffect(() => {
    if (!onChartImageChange) return;
    if (!lineChartData?.length || lineChartLoading) return;
    let cancelled = false;
    const capture = async () => {
      try {
        await new Promise((r) => setTimeout(r, 800));
        const uri = await execChartCapture('area-datetime');
        if (!cancelled && uri) {
          onChartImageChange(uri);
        }
      } catch {
        // Chart not ready
      }
    };
    capture();
    return () => { cancelled = true; };
  }, [lineChartData, lineChartLoading, onChartImageChange, execChartCapture]);

  // Capture bar chart (wertentwicklung_nach_jahren) image for PDF rendering
  useEffect(() => {
    if (!onChart1ImageChange) return;
    if (!hasBarChartData || barChartLoading) return;
    let cancelled = false;
    const capture = async () => {
      try {
        await new Promise((r) => setTimeout(r, 1200));
        const uri = await execChartCapture('bar-wertentwicklung');
        if (!cancelled && uri) {
          onChart1ImageChange(uri);
        }
      } catch {
        // Chart not ready
      }
    };
    capture();
    return () => { cancelled = true; };
  }, [hasBarChartData, barChartLoading, barChartSeries, onChart1ImageChange, execChartCapture]);

  // Capture horizontal-bar chart (gesamtportfolio) image for PDF rendering
  useEffect(() => {
    if (!onChart2ImageChange) return;
    if (isPvmGpsTemplate(template?._id)) return;
    if (!pdf?.gesamtportfolio?.length) return;
    let cancelled = false;
    const capture = async () => {
      try {
        await new Promise((r) => setTimeout(r, 1500));
        const uri = await execChartCapture('hbar-gesamtportfolio');
        if (!cancelled && uri) {
          onChart2ImageChange(uri);
        }
      } catch {
        // Chart not ready
      }
    };
    capture();
    return () => { cancelled = true; };
  }, [pdf?.gesamtportfolio, onChart2ImageChange, execChartCapture]);

  // Capture donut charts for PDF — keyed by display slot (handles renamed pie order per template)
  useEffect(() => {
    if (isPvmGpsTemplate(template?._id)) {
      let cancelled = false;
      const captureGps = async () => {
        if (pdf?.assetAllocation?.length && onPieChart1ImageChange) {
          await new Promise((r) => setTimeout(r, 1800));
          try {
            const uri = await execChartCapture(GPS_DONUT_ASSET_ID);
            if (!cancelled && uri) onPieChart1ImageChange(uri);
          } catch {
            // Chart not ready
          }
        }
        if (pdf?.sectorAllocation?.length && onPieChart2ImageChange) {
          await new Promise((r) => setTimeout(r, 300));
          try {
            const uri = await execChartCapture(GPS_DONUT_SECTOR_ID);
            if (!cancelled && uri) onPieChart2ImageChange(uri);
          } catch {
            // Chart not ready
          }
        }
      };
      captureGps();
      return () => { cancelled = true; };
    }

    const slots = getPieChartDisplayOrder(template?._id);
    const pieHandlers: Record<PiePreviewImageKey, ((dataUrl?: string) => void) | undefined> = {
      pieChart1ImageDataUrl: onPieChart1ImageChange,
      pieChart2ImageDataUrl: onPieChart2ImageChange,
      pieChart3ImageDataUrl: onPieChart3ImageChange,
      chart3ImageDataUrl: onChart3ImageChange,
    };

    let cancelled = false;
    let delay = 1800;

    const capture = async () => {
      for (const slot of slots) {
        const handler = pieHandlers[slot.previewImageKey];
        if (!handler) continue;
        const pieData = pdf?.[slot.field];
        if (!Array.isArray(pieData) || pieData.length === 0) continue;
        await new Promise((r) => setTimeout(r, delay));
        delay = 300;
        try {
          const uri = await execChartCapture(slot.donutId);
          if (!cancelled && uri) {
            handler(uri);
          }
        } catch {
          // Chart not ready
        }
      }
    };

    capture();
    return () => { cancelled = true; };
  }, [
    template?._id,
    pdf?.assetAllocation,
    pdf?.sectorAllocation,
    pdf?.branchen,
    pdf?.regionen,
    pdf?.marktkapitalisierung,
    pdf?.pie_addition,
    onPieChart1ImageChange,
    onPieChart2ImageChange,
    onPieChart3ImageChange,
    onChart3ImageChange,
    execChartCapture,
  ]);

  // Dummy editableItemClick handler (not editable in preview)
  const handleEditableItemClick = () => {
    return () => {
      // Preview mode - no editing
    };
  };

  // Default business-table icons (same order as old PDF viewer)
  const DEFAULT_ESG_ICONS = [
    './public/icon_1.png', './public/icon_8.png', './public/icon_5.png',
    './public/icon_4.png', './public/icon_10.png', './public/icon_2.png',
    './public/icon_7.png', './public/icon_3.png', './public/icon_6.png',
    './public/icon_9.png',
  ];

  // Helper function to get ESG item image (with default icon fallback)
  const getEsgItemImage = (item: any, index: number): string => {
    if (item.image) {
      return item.image.includes('./public') ? `${API_SERVER}/${item.image}` : item.image;
    }
    // Fallback to default icon based on row index
    const defaultIcon = DEFAULT_ESG_ICONS[index];
    return defaultIcon ? `${API_SERVER}/${defaultIcon}` : '';
  };

  // Render custom header matching backend PDF structure
  const renderHeader = () => {
    const templateColor = brandHeadlineColor;
    const templateFond = resolvePvmGpsFondTitle(template?._id, template?.fond);
    const titleAddition = resolveTitleAddition(
      template?._id,
      pdf?.title_addition,
      pdf?.shareClassColumns,
    );
    const publishedAt = pdf?.published_at || new Date();
    const iconUrl = template?.icon_url;

    return (
      <Box>
        {isPvmGps && (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                mb: 2.5,
              }}
            >
              <Box
                sx={{
                  color: '#949599',
                  fontSize: 16,
                  fontWeight: 'bold',
                  flex: 1,
                  pr: 2,
                }}
              >
                Werbematerial, kein investmentrechtliches Pflichtdokument
              </Box>
              <Box
                sx={{
                  width: PVM_HEADER_TOP_LOGO_WIDTH_WEB,
                  height: PVM_HEADER_TOP_LOGO_HEIGHT_WEB,
                  flexShrink: 0,
                }}
              >
                <img
                  src={PVM_GPS_RIGHT_LOGO}
                  alt="PARTNERS VermögensManagement AG"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'right center',
                    display: 'block',
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ borderTop: `1px solid ${PVM_HEADLINE_COLOR}`, width: '100%', mb: `${PVM_HEADER_PADDING_BELOW_TOP_BORDER_WEB}px` }} />
          </>
        )}
        <Box
          sx={[
            styles.headerContainer,
            ...(isPvmGps
              ? [{ alignItems: 'center' as const, position: 'relative' as const, width: '100%' }]
              : []),
          ]}
        >
        {isPvmGps ? (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
                gap: 2,
              }}
            >
              <Box sx={{ width: PVM_HEADER_LEFT_LOGO_WIDTH_WEB, flexShrink: 0 }}>
                <img
                  src={resolvePvmGpsLeftLogoUrl(iconUrl)}
                  alt="Global Premium Select"
                  style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
                <Box sx={{ ...styles.titleSectionHeader, justifyContent: 'flex-start' }}>
                  <Box sx={{ ...styles.factsFiguresText, fontFamily: PVM_BODY_FONT_STACK, color: PVM_HEADLINE_COLOR }}>
                    Facts & Figures
                  </Box>
                  <Box
                    sx={{
                      ...styles.dateText,
                      fontFamily: PVM_BODY_FONT_STACK,
                      fontWeight: 'normal',
                      color: '#949599',
                    }}
                  >
                    | {formatFactsheetHeaderDate(template?._id, publishedAt, pdf?.shareClassColumns, pdf?.preise, pdf)}
                  </Box>
                </Box>
                <Box
                  data-pvm-headline="true"
                  sx={(theme) => ({
                    ...styles.mainTitle(theme, templateColor),
                    fontSize: PVM_HEADER_TITLE_FONT_SIZE_WEB,
                    lineHeight: 1.15,
                    fontFamily: PVM_HEADLINE_FONT_STACK,
                    mt: 0.5,
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                  })}
                >
                  {templateFond}
                  {titleAddition && (
                    <Box component="span" sx={styles.titleAddition}>
                      {' '}{titleAddition}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                justifyContent: 'center',
                flexShrink: 0,
                width: PVM_HEADER_SIDE_COLUMN_WIDTH_WEB,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  fontSize: 10.5,
                  color: brandBodyColor,
                  lineHeight: 1.25,
                  fontFamily: PVM_BODY_FONT_STACK,
                }}
              >
                {PVM_GPS_CONTACT_LINES.map((line, i) => (
                  <Box
                    key={i}
                    sx={{
                      textAlign: 'right',
                      width: '100%',
                      display: 'block',
                      fontWeight: line.bold ? 'bold' : 'normal',
                    }}
                  >
                    {line.text}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : (
        <Stack direction="row" spacing={3.75} alignItems="center">
          <Box sx={styles.logoSectionContainer}>
              <Box sx={(theme) => styles.logoBoxTop(theme, templateColor)} />
              <Box sx={(theme) => styles.logoBoxBottom(theme, templateColor)}>
                {iconUrl && (
                  <img src={iconUrl} alt="logo" style={inlineStyles.logoImage} />
                )}
              </Box>
            </Box>
          <Box sx={styles.titleSectionContainer}>
            <Box sx={styles.titleSectionHeader}>
              <Box sx={styles.factsFiguresText}>Facts & Figures</Box>
              <Box sx={styles.dateText}>
                | {formatFactsheetHeaderDate(template?._id, publishedAt, pdf?.shareClassColumns, pdf?.preise, pdf)}
              </Box>
            </Box>
            <Box
              sx={(theme) => ({
                ...styles.mainTitle(theme, templateColor),
              })}
            >
              {templateFond}
              {titleAddition && (
                <Box component="span" sx={styles.titleAddition}>
                  {' '}| {titleAddition}
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
        )}
        {!isPvmGps && (
        <Stack direction="row" spacing={3.75} alignItems="center">
              <Box sx={styles.contactSection}>
                <Box sx={styles.contactBoldText}>Shareholder Value</Box>
                <Box sx={styles.contactBoldText}>Management AG</Box>
                <Box>Neue Mainzer Straße 1</Box>
                <Box>D - 60311 Frankfurt am Main</Box>
                <Box>Tel. +49 (0)69 66 98 30 0</Box>
                <Box>info@shareholdervalue.de</Box>
                <Box sx={styles.contactBoldText}>www.shareholdervalue.de</Box>
              </Box>
              <Box sx={styles.rightLogoContainer}>
                <img
                  src="/images/header-2.png"
                  alt="header-2"
                  style={inlineStyles.rightLogoImage}
                />
              </Box>
        </Stack>
        )}
        </Box>
        {isPvmGps && (
          <Box sx={{ borderBottom: `1px solid ${PVM_HEADLINE_COLOR}`, width: '100%' }} />
        )}
      </Box>
    );
  };

  return (
    <Stack spacing={page1StackSpacing}>
      {/* First Page */}
      <Box sx={paperSx}>
        {!isPvmGps && (
          <Box sx={(theme) => styles.disclaimerText(theme, 16)}>
            Werbematerial, kein investmentrechtliches Pflichtdokument
          </Box>
        )}
        {renderHeader()}

        {/* Page 1 hero: same copy as MONATSKOMMENTAR when set; else legacy intro[0]/[1] */}
        {(monthlyCommentaryDisplay.left || monthlyCommentaryDisplay.right) ? (
          <Box
            id="monthly-commentary-section"
            sx={(theme) =>
              page1Compact
                ? { ...styles.intro(theme), m: page1Compact.introMargin }
                : styles.intro(theme)
            }
          >
            <Box sx={styles.introRow}>
              {showIntroManImage && (
                <Box
                  sx={(theme) =>
                    styles.introImage(
                      theme,
                      calcMonthlyCommentaryImageWidthWeb(
                        monthlyCommentaryDisplay.left,
                        monthlyCommentaryDisplay.right,
                      ),
                    )
                  }
                >
                  <img
                    src={manImageUrlResolved}
                    alt=""
                  />
                </Box>
              )}
              <Box
                sx={(theme) =>
                  isPvmGps
                    ? { ...styles.introTextColumns(theme), gap: 12, alignItems: 'flex-start' }
                    : styles.introTextColumns(theme)
                }
              >
                <Box sx={[introTextSx, { flex: 1, minWidth: 0, mt: 0, pt: 0 }]} lang="de">
                  {monthlyCommentaryDisplay.left}
                </Box>
                <Box sx={[introTextSx, { flex: 1, minWidth: 0, mt: 0, pt: 0 }]} lang="de">
                  {monthlyCommentaryDisplay.right}
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          pdf?.intro &&
          pdf.intro.length >= 2 && (
            <Box
              id="intro-section"
              sx={(theme) =>
                page1Compact
                  ? { ...styles.intro(theme), m: page1Compact.introMargin }
                  : styles.intro(theme)
              }
            >
              <Box sx={styles.introRow}>
                {showIntroManImage && (
                  <Box
                    sx={(theme) =>
                      styles.introImage(
                        theme,
                        calcMonthlyCommentaryImageWidthWeb(
                          String(pdf.intro?.[0] ?? ''),
                          String(pdf.intro?.[1] ?? ''),
                        ),
                      )
                    }
                  >
                    <img
                      src={manImageUrlResolved}
                      alt="intro"
                    />
                  </Box>
                )}
                <Box sx={styles.introTextColumns}>
                  <Box sx={[introTextSx, { flex: 1 }]} lang="de">
                    {pdf.intro[0]}
                  </Box>
                  <Box sx={[introTextSx, { flex: 1 }]} lang="de">
                    {pdf.intro[1]}
                  </Box>
                </Box>
              </Box>
            </Box>
          )
        )}

        {/* Two Column Layout */}
        <Grid
          container
          spacing={page1GridSpacing}
          sx={isPvmGps ? { alignItems: 'stretch' } : undefined}
        >
          {/* Left Column */}
          <Grid item xs={5}>
            {isPvmGps ? (
              <PvmPage1LeftColumnWeb
                pdf={pvmLeftColumnPdf}
                contentTitle2Sx={contentTitle2Sx as (template?: unknown) => object}
                template={template}
                compact={Boolean(page1Compact)}
                investmentphilosophieTitle={sectionHeaders.investmentphilosophie}
                strategieTitle={sectionHeaders.strategie}
                onTitleClick={handleEditableItemClick}
              />
            ) : (
            <>
            {/* Investment Philosophy */}
            <Box id="investmentphilosophie-section" sx={(theme) => ({ ...styles.widget(theme), mb: page1SectionMb })}>
              <Typography
                sx={page1Compact ? compactContentTitleSx : contentTitleSx}
                onClick={handleEditableItemClick}
              >
                {sectionHeaders.investmentphilosophie}
              </Typography>
              <Box sx={{ ...(typeof contentTextSx === 'function' ? (contentTextSx as () => object)() : contentTextSx), mb: 0 }}>
                {pdf?.investmentphilosophie || ''}
              </Box>
            </Box>

            {/* Strategie */}
            <Box id="strategie-section" sx={(theme) => ({ ...styles.widget(theme), mb: page1SectionMb })}>
              <Box
                sx={() => ({
                  ...contentTitle2Sx(template),
                  mb: page1Compact ? page1Compact.contentTitle2MarginBottom : 0,
                  pb: page1Compact ? page1Compact.contentTitle2PaddingBottom : 2,
                })}
                onClick={handleEditableItemClick}
              >
                {sectionHeaders.strategie}
              </Box>
              <Box sx={{ ...(typeof contentTextSx === 'function' ? (contentTextSx as () => object)() : contentTextSx), mb: 0 }}>
                {pdf?.strategie || ''}
              </Box>
            </Box>

            {dualShareClassLayout && dualShareColumnsData?.[0] && dualShareColumnsData[1] ? (
              <>
                <Box id="preise-section" sx={(theme) => ({ ...styles.widget(theme), mb: page1SectionMb })}>
                  <Box
                    sx={() => ({
                      ...contentTitle2Sx(template),
                      mb: page1Compact ? page1Compact.contentTitle2MarginBottom : undefined,
                      pb: page1Compact ? page1Compact.contentTitle2PaddingBottom : undefined,
                    })}
                    onClick={handleEditableItemClick}
                  >
                    {sectionHeaders.preise}
                  </Box>
                  <Box sx={{ mt: page1Compact ? 0.35 : 0.5 }}>
                    {(() => {
                      const visible = getVisibleColumnIndices();
                      if (visible.length === 2) {
                        return (
                          <Box sx={dualSharePreviewTriple.row}>
                            <Box sx={dualSharePreviewTriple.label} />
                            <Box sx={dualSharePreviewTriple.header}>{dualShareColumnsData[visible[0]].label || '—'}</Box>
                            <Box sx={dualSharePreviewTriple.header}>{dualShareColumnsData[visible[1]].label || '—'}</Box>
                          </Box>
                        );
                      }
                      return (
                        <Box sx={dualSharePreviewTriple.row}>
                          <Box sx={dualSharePreviewTriple.label} />
                          <Box sx={dualSharePreviewTriple.singleHeader}>{dualShareColumnsData[visible[0]].label || '—'}</Box>
                        </Box>
                      );
                    })()}
                    {(() => {
                      const visible = getVisibleColumnIndices();
                      const p0 = buildPreiseDisplay(dualShareColumnsData[visible[0]].preise || {}, `sc${visible[0]}`);
                      const p1 = visible.length === 2
                        ? buildPreiseDisplay(dualShareColumnsData[visible[1]].preise || {}, `sc${visible[1]}`)
                        : null;
                      const rows: React.ReactNode[] = [];
                      getPreiseDisplayRows(template?._id).forEach(([key, label]) => {
                        const raw0 = key === 'Letzte Ausschüttung' ? p0['Letzte Ausschüttung'] : (p0 as any)[key];
                        const raw1 = p1 ? (key === 'Letzte Ausschüttung' ? p1['Letzte Ausschüttung'] : (p1 as any)[key]) : '';
                        const fk = key === 'Letzte Ausschüttung' ? 'letzte' : key;
                        const d0 = formatPreiseCell(fk, raw0);
                        const d1 = formatPreiseCell(fk, raw1);
                        if (visible.length === 2 && isDualRowHidden(d0, d1)) return;
                        if (visible.length === 1 && isFactsheetDisplayEmpty(d0)) return;
                        rows.push(
                          <Box key={key} sx={dualSharePreviewTriple.row}>
                            <Box sx={dualSharePreviewTriple.label}>{label}</Box>
                            <Box sx={visible.length === 2 ? dualSharePreviewTriple.value : dualSharePreviewTriple.singleValue}>{d0}</Box>
                            {visible.length === 2 ? <Box sx={dualSharePreviewTriple.value}>{d1}</Box> : null}
                          </Box>,
                        );
                      });
                      return rows;
                    })()}
                  </Box>
                </Box>

                <Box id="konditionen-section" sx={(theme) => ({ ...styles.widget(theme), mb: page1SectionMb })}>
                  <Box
                    sx={() => ({
                      ...contentTitle2Sx(template),
                      mb: page1Compact ? page1Compact.contentTitle2MarginBottom : undefined,
                      pb: page1Compact ? page1Compact.contentTitle2PaddingBottom : undefined,
                    })}
                    onClick={handleEditableItemClick}
                  >
                    {sectionHeaders.konditionen}
                  </Box>
                  <Box sx={{ mt: page1Compact ? 0.35 : 0.5 }}>
                    {(() => {
                      const visible = getVisibleColumnIndices();
                      if (visible.length === 2) {
                        return (
                          <Box sx={dualSharePreviewTriple.row}>
                            <Box sx={dualSharePreviewTriple.label} />
                            <Box sx={dualSharePreviewTriple.header}>{dualShareColumnsData[visible[0]].label || '—'}</Box>
                            <Box sx={dualSharePreviewTriple.header}>{dualShareColumnsData[visible[1]].label || '—'}</Box>
                          </Box>
                        );
                      }
                      return (
                        <Box sx={dualSharePreviewTriple.row}>
                          <Box sx={dualSharePreviewTriple.label} />
                          <Box sx={dualSharePreviewTriple.singleHeader}>{dualShareColumnsData[visible[0]].label || '—'}</Box>
                        </Box>
                      );
                    })()}
                    {(
                      [
                        ['laufendeKosten', 'Laufende Kosten:'],
                        ['verwaltungsgebühr', 'Verwaltungsgebühr:'],
                        ['verwaltungsvergütung', 'Verwaltungsvergütung:'],
                        ['performance_fee', 'Performance Fee:'],
                        ['ter_2022', KONDITIONEN_TER_ROW_LABEL],
                        ['ausgabeaufschlag', 'Ausgabeaufschlag:'],
                      ] as const
                    ).flatMap(([k, label]) => {
                      const visible = getVisibleColumnIndices();
                      const d0 = String((dualShareColumnsData[visible[0]].konditionen as any)?.[k] ?? '').replace(
                        /\r?\n+/g,
                        ' ',
                      );
                      const d1 = String((dualShareColumnsData[visible[1]]?.konditionen as any)?.[k] ?? '').replace(
                        /\r?\n+/g,
                        ' ',
                      );
                      if (visible.length === 2 && isDualRowHidden(d0, d1)) return [];
                      if (visible.length === 1 && isFactsheetDisplayEmpty(d0)) return [];
                      return [
                        <Box key={k} sx={dualSharePreviewTriple.row}>
                          <Box sx={dualSharePreviewTriple.label}>{label}</Box>
                          <Box sx={visible.length === 2 ? dualSharePreviewTriple.value : dualSharePreviewTriple.singleValue}>{d0.trim()}</Box>
                          {visible.length === 2 ? <Box sx={dualSharePreviewTriple.value}>{d1.trim()}</Box> : null}
                        </Box>,
                      ];
                    })}
                  </Box>
                </Box>

                <Box id="stammdaten-section" sx={(theme) => ({ ...styles.widget(theme), mb: 0 })}>
                  <Box
                    sx={() => ({
                      ...contentTitle2Sx(template),
                      mb: page1Compact ? page1Compact.contentTitle2MarginBottom : undefined,
                      pb: page1Compact ? page1Compact.contentTitle2PaddingBottom : undefined,
                    })}
                    onClick={handleEditableItemClick}
                  >
                    {sectionHeaders.stammdaten}
                  </Box>
                  <Box sx={{ mt: page1Compact ? 0.25 : 0.5, '& > *:last-child': { mb: 0 } }}>
                    {(() => {
                      const visible = getVisibleColumnIndices();
                      if (visible.length === 2) {
                        return (
                          <Box sx={dualSharePreviewTriple.row}>
                            <Box sx={dualSharePreviewTriple.label} />
                            <Box sx={dualSharePreviewTriple.header}>{dualShareColumnsData[visible[0]].label || '—'}</Box>
                            <Box sx={dualSharePreviewTriple.header}>{dualShareColumnsData[visible[1]].label || '—'}</Box>
                          </Box>
                        );
                      }
                      return (
                        <Box sx={dualSharePreviewTriple.row}>
                          <Box sx={dualSharePreviewTriple.label} />
                          <Box sx={dualSharePreviewTriple.singleHeader}>{dualShareColumnsData[visible[0]].label || '—'}</Box>
                        </Box>
                      );
                    })()}
                    {(
                      [
                        ['isin', 'ISIN:'],
                        ['wkn', 'WKN:'],
                        ['bloomberg', 'Bloomberg:'],
                        ['reuters', 'Reuters:'],
                        ['fondsart', 'Fondsart:'],
                        ['nachhaltigkeit', 'Nachhaltigkeit:'],
                        ['anlageschwerpunkt', 'Anlageschwerpunkt:'],
                        ['srri', 'SRI:'],
                        ['vergleichsindex', 'Vergleichsindex:'],
                        ['auflagedatum', 'Auflagedatum:'],
                        ['anlageberater', 'Anlageberater:'],
                        [
                          'verwaltungsgesellschaft',
                          getStammdatenRowLabel(template?._id, 'verwaltungsgesellschaft', 'Verwaltungsgesellschaft:'),
                        ],
                        ['verwahrstelle', 'Verwahrstelle:'],
                        ['geschäftsjahr', 'Geschäftsjahr:'],
                        ['vertriebszulassung', 'Vertriebszulassung:'],
                      ] as const
                    ).flatMap(([k, label]) => {
                      if (k === 'anlageberater' && !templateShowsAnlageberater(template?._id)) return [];
                      const visible = getVisibleColumnIndices();
                      const fmt = (v: unknown) => {
                        if (k === 'auflagedatum' && v) {
                          const m = moment(v as string);
                          return m.isValid() ? m.format('DD.MM.YYYY') : String(v);
                        }
                        if (v === undefined || v === null) return '';
                        if (k === 'anlageberater') return normalizeMultilineFieldValue(v).trim();
                        return String(v);
                      };
                      const v0 = (dualShareColumnsData[visible[0]].stammdaten as any)?.[k];
                      const v1 = (dualShareColumnsData[visible[1]]?.stammdaten as any)?.[k];
                      const d0 = fmt(v0);
                      const d1 = fmt(v1);
                      if (visible.length === 2 && isDualRowHidden(d0, d1)) return [];
                      if (visible.length === 1 && isFactsheetDisplayEmpty(d0)) return [];
                      return [
                        <Box key={k} sx={dualSharePreviewTriple.row}>
                          <Box sx={dualSharePreviewTriple.label}>{label}</Box>
                          <Box sx={visible.length === 2 ? dualSharePreviewTriple.value : dualSharePreviewTriple.singleValue}>{d0}</Box>
                          {visible.length === 2 ? <Box sx={dualSharePreviewTriple.value}>{d1}</Box> : null}
                        </Box>,
                      ];
                    })}
                  </Box>
                </Box>
              </>
            ) : (
              <>
                {/* Prices */}
                <Box id="preise-section" sx={(theme) => ({ ...styles.widget(theme), mb: page1Compact ? page1SectionMb : 20 })}>
                  <Box
                    sx={() => ({
                      ...contentTitle2Sx(template),
                      mb: page1Compact ? page1Compact.contentTitle2MarginBottom : undefined,
                      pb: page1Compact ? page1Compact.contentTitle2PaddingBottom : undefined,
                    })}
                    onClick={handleEditableItemClick}
                  >
                    {sectionHeaders.preise}
                  </Box>
                  <Box sx={{ mt: page1Compact ? 0.25 : 0, ...(isPvmGps ? pvmWebDataTableSx : {}) }}>
                    <DataTable data={resolvedPreise || {}} />
                  </Box>
                </Box>

                {/* Conditions */}
                <Box id="konditionen-section" sx={(theme) => ({ ...styles.widget(theme), mb: page1Compact ? page1SectionMb : 20 })}>
                  <Box
                    sx={() => ({
                      ...contentTitle2Sx(template),
                      mb: page1Compact ? page1Compact.contentTitle2MarginBottom : undefined,
                      pb: page1Compact ? page1Compact.contentTitle2PaddingBottom : undefined,
                    })}
                    onClick={handleEditableItemClick}
                  >
                    {sectionHeaders.konditionen}
                  </Box>
                  <Box sx={{ mt: page1Compact ? 0.25 : 0, ...(isPvmGps ? pvmWebDataTableSx : {}) }}>
                    <DataTable data={resolvedKonditionen || {}} />
                  </Box>
                </Box>

                {/* Master Data */}
                <Box id="stammdaten-section" sx={(theme) => ({ ...styles.widget(theme), mb: 0 })}>
                  <Box
                    sx={() => ({
                      ...contentTitle2Sx(template),
                      mb: page1Compact ? page1Compact.contentTitle2MarginBottom : undefined,
                      pb: page1Compact ? page1Compact.contentTitle2PaddingBottom : undefined,
                    })}
                    onClick={handleEditableItemClick}
                  >
                    {sectionHeaders.stammdaten}
                  </Box>
                  <Box sx={{ mt: page1Compact ? 0.25 : 0, '& table tbody tr:last-of-type .MuiTableCell-root': { borderBottom: 0, pb: 0 }, ...(isPvmGps ? pvmWebDataTableSx : {}) }}>
                    <DataTable data={resolvedStammdaten || {}} />
                  </Box>
                </Box>
              </>
            )}
            </>
            )}
          </Grid>

          {/* Right Column */}
          <Grid
            item
            xs={7}
            sx={
              isPvmGps
                ? { display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }
                : undefined
            }
          >
            {/* Performance Since Launch */}
            <Box id="wertentwicklung-seit-auflage-section" sx={(theme) => ({ ...styles.widget(theme), mb: page1Compact ? page1SectionMb : undefined })}>
              <Typography
                sx={page1Compact ? compactContentTitleSx : contentTitleSx}
                onClick={handleEditableItemClick}
              >
                {sectionHeaders.wertentwicklungSeitAuflage}
              </Typography>
              <ApexLineChart
                data={lineChartData}
                isSolActive={!!template?.isins?.length}
                color={brandHeadlineColor}
                loading={lineChartLoading}
                showToolbar={false}
                fillType="gradient"
                fillOpacityFrom={0.35}
                fillOpacityTo={0.05}
                fillShadeIntensity={1}
                height={page1LineChartHeight}
                background="transparent"
                seriesName={historyLegendLabel}
                showLegend={false}
                yAxisPrefix=""
                yAxisFormatter={(value: number) => {
                  const rounded = Math.round(value);
                  const formatted = Number.isFinite(rounded) ? rounded.toLocaleString('de-DE') : String(value);
                  return `${formatted} ${historyCurrencySymbol || ''}`.trim();
                }}
              />
              {compactLayout && fafsHistoryTableLabel?.label ? (
                <Box
                  sx={(theme) => ({
                    ...styles.historyChartLegend(theme, brandHeadlineColor),
                    alignItems: 'center',
                  })}
                >
                  <Box component="span" className="history-chart-legend-line" sx={{ flexShrink: 0 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                    <Box component="span">{fafsHistoryTableLabel.label}</Box>
                    {fafsHistoryTableLabel.subLabel ? (
                      <Box component="span">{fafsHistoryTableLabel.subLabel}</Box>
                    ) : null}
                  </Box>
                </Box>
              ) : historyLegendLabel ? (
                <Box sx={(theme) => styles.historyChartLegend(theme, brandHeadlineColor)}>
                  <Box component="span" className="history-chart-legend-line" />
                  {historyLegendLabel}
                </Box>
              ) : null}
            </Box>
            {/* Performance By Years */}
            <Box id="wertentwicklung-nach-jahren-section" sx={(theme) => ({ ...styles.widget(theme), mb: page1Compact ? page1SectionMb : undefined })}>
              <Typography
                sx={page1Compact ? compactContentTitleSx : contentTitleSx}
                onClick={handleEditableItemClick}
              >
                {sectionHeaders.wertentwicklungNachJahren}
              </Typography>
              {isBarAutomated ? (
                <Box sx={{ position: 'relative', minHeight: page1BarChartHeight }}>
                  {barChartLoading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#00000008',
                      }}
                    >
                      <CircularProgress size={36} sx={{ color: brandHeadlineColor }} />
                    </Box>
                  )}
                  {hasBarChartData ? (
                    <ReactApexChart
                      type="bar"
                      height={page1BarChartHeight}
                      options={barChartOptions}
                      series={barChartSeries}
                    />
                  ) : (
                    <Box sx={{ height: page1BarChartHeight }} />
                  )}
                </Box>
              ) : (
                pdf?.wertentwicklung_nach_jahren &&
                pdf.wertentwicklung_nach_jahren.length > 0 && (
                  <Chart
                    id="bar-wertentwicklung"
                    data={pdf.wertentwicklung_nach_jahren}
                    type="bar"
                    name="WERTENTWICKLUNG NACH JAHREN (%)"
                    template={template}
                    height={page1BarChartHeight}
                  />
                )
              )}
            </Box>
            {!isPvmGps && (
              <Box sx={styles.widget}>
                <Box sx={(theme) => styles.chartsLabel(theme, brandHeadlineColor)}>
                  {pdf?.wertentwicklung_nach_jahren_label || 'Frankfurter Modern Value Index'}
                </Box>
              </Box>
            )}

            {/* Performance By Periods */}
            {pdf?.wertentwicklung_nach_perioden && (() => {
              const periodsData = pdf.wertentwicklung_nach_perioden;
              let labels: string[] = [];
              let rawValues: any[][] = [];
              const columns = periodsData.columns || [];

              if (periodsData.labels) {
                labels = periodsData.labels;
                rawValues = Array.isArray(periodsData.values) ? periodsData.values : [];
              } else if (Array.isArray(periodsData)) {
                labels = periodsData.map((item: any) => item.label);
                rawValues = [periodsData.map((item: any) => String(item.value))];
              }

              if (labels.length === 0) return null;

              return (
                <Box
                  id="wertentwicklung-nach-perioden-section"
                  sx={(theme) => ({
                    ...styles.widget(theme),
                    mb: page1Compact ? page1SectionMb : undefined,
                  })}
                >
                  <Typography sx={contentTitleSx} onClick={handleEditableItemClick}>
                    {sectionHeaders.wertentwicklungNachPerioden}
                  </Typography>
                  {/* Header row */}
                  <Stack direction="row" sx={{ width: '100%', mt: 4, borderBottom: '1px solid #a7a8ab' }}>
                    {labels.map((label: string, i: number) => (
                      <Box key={i} sx={{ flex: 1, textAlign: i === 0 ? 'left' : 'center' }}>
                        <Box sx={{ color: '#000', fontWeight: 'bold' }}>{label}</Box>
                      </Box>
                    ))}
                  </Stack>
                  {/* Value rows */}
                  {rawValues.map((row: any[], rowIdx: number) => (
                    <Stack key={rowIdx} direction="row" spacing={0.5} sx={{ width: '100%', mt: 0.5 }}>
                      {row.map((cell: any, i: number) => {
                        const displayValue = resolveCellDisplay(cell, 'perf', rowIdx, i, columns);
                        return (
                          <Box key={i} sx={{
                            flex: 1,
                            px: 1,
                            py: 0.25,
                            background: '#D2D2D4',
                            textAlign: i === 0 ? 'left' : 'center',
                          }}>
                            <Box sx={{ color: '#000' }}>{displayValue}</Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  ))}
                </Box>
              );
            })()}

            {/* Risk Indicators */}
            {pdf?.risikoindikatoren?.labels?.length > 0 && (() => {
              const riskColumns = pdf.risikoindikatoren.columns || [];
              return (
                <Box id="risikoindikatoren-section" sx={styles.widget}>
                  <Typography sx={contentTitleSx} onClick={handleEditableItemClick}>
                    {sectionHeaders.risikoindikatoren}
                  </Typography>
                  {/* Header row */}
                  <Stack direction="row" sx={{ width: '100%', mt: 4, borderBottom: '1px solid #a7a8ab' }}>
                    {pdf.risikoindikatoren.labels.map((item: any, key: number) => (
                      <Box key={key} sx={{ flex: 1, textAlign: key === 0 ? 'left' : 'center' }}>
                        <Box sx={{ color: '#000', fontWeight: 'bold' }}>{item}</Box>
                      </Box>
                    ))}
                  </Stack>
                  {/* Value rows */}
                  {Array.isArray(pdf.risikoindikatoren.values) && pdf.risikoindikatoren.values.map((row: any[], index: number) => (
                    <Stack key={index} direction="row" spacing={0.5} sx={{ width: '100%', mt: 0.5 }}>
                      {Array.isArray(row) && row.map((cell: any, columnIndex: number) => {
                        const displayValue = resolveCellDisplay(cell, 'risk', index, columnIndex, riskColumns);
                        return (
                          <Box key={columnIndex} sx={{
                            flex: 1,
                            px: 1,
                            py: 0.25,
                            background: '#D2D2D4',
                            textAlign: columnIndex === 0 ? 'left' : 'center',
                          }}>
                            <Box sx={{ color: '#000' }}>{displayValue}</Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  ))}
                </Box>
              );
            })()}

            {/* Footer Notes — PVM: pin to bottom of right column */}
            <Box
              id="fussnote-section"
              sx={(theme) => ({
                ...styles.footerNotes(theme),
                ...(isPvmGps ? { mt: 'auto', pt: 1.5 } : {}),
              })}
            >
              {resolveFussnoteText(pdf?.fussnote).split('\n').map((line, index, lines) => (
                <React.Fragment key={index}>
                  {line}
                  {index < lines.length - 1 ? <br /> : null}
                </React.Fragment>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Second Page */}
      <Box
        sx={(theme) => ({
          ...paperSx(theme),
          ...(isPvmGps
            ? {
                display: 'flex',
                flexDirection: 'column',
                // Approximate A4 preview height so disclaimer can sit near the bottom
                minHeight: 1123,
              }
            : {}),
        })}
      >
        {!isPvmGps && (
          <Box sx={(theme) => styles.disclaimerText(theme, 16)}>
            Werbematerial, kein investmentrechtliches Pflichtdokument
          </Box>
        )}
        {renderHeader()}

        {isPvmGps ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: `${page2GridSpacing}px`,
              width: '100%',
              mt: 0,
            }}
          >
            <Box sx={{ width: '34%', maxWidth: 280, flexShrink: 0, minWidth: 0 }}>
              <PvmPage2LeftWeb
                chancen={pdf?.chancen || ''}
                risiken={pdf?.risiken || ''}
                chancenTitle={sectionHeaders.chancen}
                risikenTitle={sectionHeaders.risiken}
                titleSx={contentTitle2Sx as (theme?: unknown) => object}
                onTitleClick={handleEditableItemClick}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <GpsPage2RightColumn
                pdf={pdf}
                sectionHeaders={sectionHeaders}
                colorSets={colorSets}
                handleEditableItemClick={handleEditableItemClick}
                compactLayout={Boolean(page1Compact)}
                sectionTitleSx={compactContentTitleSx}
              />
            </Box>
          </Box>
        ) : (
        <Grid mt={0} container spacing={page2GridSpacing} alignItems="flex-start">
          {/* Left Column */}
          <Grid item xs={5} sx={{ alignSelf: 'flex-start', minWidth: 0 }}>
            <>
            <Box id="chancen-section" sx={styles.widget}>
              <Box sx={() => contentTitle2Sx(template)} onClick={handleEditableItemClick}>
                {sectionHeaders.chancen}
              </Box>
              <Box sx={contentTextSx}>
                {pdf?.chancen || ''}
              </Box>
            </Box>
            <Box id="risiken-section" sx={styles.widget}>
              <Box sx={() => contentTitle2Sx(template)} onClick={handleEditableItemClick}>
                {sectionHeaders.risiken}
              </Box>
              <Box sx={contentTextSx}>
                {pdf?.risiken || ''}
              </Box>
            </Box>
            {!omitEthikKontroverse && (
              <>
                <Box id="ethik-section" sx={styles.widget}>
                  <Box sx={() => contentTitle2Sx(template)} onClick={handleEditableItemClick}>
                    {sectionHeaders.ethik}
                  </Box>
                  <Box sx={contentTextSx}>
                    {pdf?.ethik || ''}
                  </Box>
                </Box>

                {/* Business Table - Kontroverse Geschäftsfelder */}
                {Array.isArray(pdf?.business_table) && pdf.business_table.length > 0 && (
                  <Box id="business-table-section" sx={styles.widget}>
                    <Box sx={() => contentTitle2Sx(template)} onClick={handleEditableItemClick}>
                      {sectionHeaders.businessTable}
                    </Box>
                    <Grid container spacing={0} columns={50} sx={styles.businessTableContainer}>
                      {/* Header row */}
                      <Grid item xs={5} sx={styles.businessTableHeaderCell} />
                      <Grid item xs={22} sx={styles.businessTableHeaderCell}>
                        <Typography>Ausschlusskriterien</Typography>
                      </Grid>
                      <Grid item xs={12} sx={styles.businessTableHeaderCell}>
                        <Typography>Kein Umsatz ❶</Typography>
                      </Grid>
                      <Grid item xs={11} sx={styles.businessTableHeaderCell}>
                        <Typography>{'Umsatz < 5% ❷'}</Typography>
                      </Grid>

                      {/* Body rows - all items in single column */}
                      {pdf.business_table.map((item: any, index: number) => (
                        <React.Fragment key={index}>
                          <Grid item xs={5} sx={styles.businessTableBodyCell}>
                            {getEsgItemImage(item, index) && <img src={getEsgItemImage(item, index)} alt={item.label} />}
                          </Grid>
                          <Grid item xs={22} sx={styles.businessTableBodyCell}>
                            <Typography>{item.label}</Typography>
                          </Grid>
                          <Grid item xs={12} sx={styles.businessTableBodyCell}>
                            <Typography>{item.no_turnover}%</Typography>
                          </Grid>
                          <Grid item xs={11} sx={styles.businessTableBodyCell}>
                            <Typography>{item.sales_volume}%</Typography>
                          </Grid>
                        </React.Fragment>
                      ))}
                    </Grid>
                  </Box>
                )}
              </>
            )}
            </>
          </Grid>

          {/* Right Column - Charts */}
          <Grid item xs={7} sx={{ alignSelf: 'flex-start', minWidth: 0 }}>
              <>
                {/* Top 5 Stocks */}
                {pdf?.gesamtportfolio && pdf.gesamtportfolio.length > 0 && (
                  <Box sx={{ mb: '24px' }}>
                    <Box sx={contentTitleSx} onClick={handleEditableItemClick}>
                      {pieHeading('gesamtportfolio')}
                    </Box>
                    <CustomHorizontalBar
                      data={pdf.gesamtportfolio}
                      id="hbar-gesamtportfolio"
                      color={brandHeadlineColor}
                    />
                  </Box>
                )}

                {getPieChartDisplayOrder(template?._id).map((slot) => {
                  const pieData = pdf?.[slot.field];
                  if (!Array.isArray(pieData) || pieData.length === 0) return null;
                  return (
                    <Box
                      key={slot.field}
                      sx={{ mb: page1Compact ? page1Compact.pieChartMarginBottom : '24px' }}
                    >
                      <Box sx={contentTitleSx} onClick={handleEditableItemClick}>
                        {pieHeading(slot.key)}
                      </Box>
                      <CustomDonutChart
                        data={pieData}
                        id={slot.donutId}
                        colors={colorSets.length > 0 ? colorSets : undefined}
                      />
                    </Box>
                  );
                })}
              </>
          </Grid>
        </Grid>
        )}

        {isPvmGps ? (
          <PvmPage2AwardImagesRowWeb
            imageUrl={pdf?.page2ImageUrl as string | undefined}
            imageUrl2={pdf?.page2ImageUrl2 as string | undefined}
            imageUrl3={pdf?.page2ImageUrl3 as string | undefined}
          />
        ) : null}

        {/* Disclaimer */}
        <Box
          id="disclaimer-section"
          sx={(theme) => ({
            ...styles.disclaimerFullText(theme, brandHeadlineColor),
            ...(isPvmGps
              ? {
                  mt: 'auto',
                  pt: 3,
                }
              : {}),
          })}
        >
          <span><b>Disclaimer:</b></span> {resolveDisclaimerText(pdf?.disclaimer)}
        </Box>
      </Box>

    </Stack>
  );
};
